import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { PrintLetterHead, useLetterHead } from '../../components/ui/PrintLetterHead';
import { useStoreIssue } from './store.queries';

const dash = (value: unknown): string => (value === null || value === undefined || value === '' ? '-' : String(value));
const ISSUE_STATUS_LABELS: Record<string, string> = { complete: 'Issued', incomplete: 'Incomplete' };
const issueStatusLabel = (value: unknown): string => ISSUE_STATUS_LABELS[String(value)] ?? dash(value);
const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
const formatPrintDateTime = (value: unknown): string => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';
  const parts = Object.fromEntries(dateTimeFormatter.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute} ${(parts.dayPeriod ?? '').toUpperCase()}`;
};
const formatMoney = (value: number): string => value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const asRecord = (value: unknown): Record<string, unknown> | null => (value && typeof value === 'object' ? value as Record<string, unknown> : null);

export const StoreIssuePrintPage = () => {
  const { id } = useParams();
  const letterHead = useLetterHead();
  const query = useStoreIssue(id ?? '');

  useEffect(() => {
    if (letterHead.isReady && query.data) window.setTimeout(() => window.print(), 300);
  }, [letterHead.isReady, query.data]);

  if (query.isLoading || !letterHead.isReady) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Issue not found" message="The issue could not be loaded." />;

  const row = query.data;
  const department = asRecord(row.department);
  const requisition = asRecord(row.requisition);
  const generatedBy = asRecord(row.generatedBy);
  const lines = Array.isArray(row.lines) ? row.lines as Record<string, unknown>[] : [];
  const grandTotal = lines.reduce((sum, line) => sum + Number(line.amount ?? 0), 0);
  const issueRows: Record<string, unknown>[] = lines.map((line) => {
    const item = asRecord(line.item);
    const unit = asRecord(item?.unit);
    const subUnit = asRecord(item?.subUnit);
    return { ...line, unitName: line.unitName ?? unit?.name, subUnitName: line.subUnitName ?? subUnit?.name };
  });

  return (
    <div className="print-requisition-page">
      <div className="print-requisition-toolbar no-print">
        <button type="button" onClick={() => window.print()}>Print</button>
        <button type="button" className="danger" onClick={() => window.close()}>Close</button>
      </div>

      <PrintLetterHead imageUrl={letterHead.imageUrl} />
      <div className="print-requisition-header">
        <h2>Store Item Issue</h2>
      </div>

      <table className="print-requisition-header-table"><tbody>
        <tr>
          <td><strong>Issue No :</strong> {dash(row.issueNumber)}</td>
          <td><strong>Issue Date :</strong> {formatPrintDateTime(row.issueDate)}</td>
          <td><strong>Issue Department :</strong> <span className="print-requisition-link">{dash(department?.name ?? department?.departmentName)}</span></td>
        </tr>
        <tr>
          <td><strong>Requisition ID :</strong> {dash(requisition?.requisitionNumber)}</td>
          <td><strong>Generated By :</strong> {dash(generatedBy?.name)}</td>
          <td><strong>Status :</strong> <span className="print-requisition-status">{issueStatusLabel(row.completionStatus)}</span></td>
        </tr>
      </tbody></table>

      <table className="print-requisition-items">
        <thead><tr><th>#</th><th>Item Name</th><th>Batch No.</th><th>Exp. Date</th><th>Quantity</th><th>Amount (₹)</th></tr></thead>
        <tbody>
          {issueRows.map((line, index) => (
            <tr key={dash(line._id ?? index)}>
              <td>{index + 1}</td>
              <td>{dash(line.itemName)}</td>
              <td>{dash(line.batchNumber)}</td>
              <td>{line.expiryDate ? formatPrintDateTime(line.expiryDate) : 'N/A'}</td>
              <td>{dash(line.quantity)}{line.unitName ? ` ${line.unitName}` : ''}{line.subUnitQuantity ? ` ${line.subUnitQuantity} ${line.subUnitName ?? ''}` : ''}</td>
              <td>{formatMoney(Number(line.amount ?? 0))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><th colSpan={5} className="print-requisition-total-label">Grand Total</th><th>{formatMoney(grandTotal)}</th></tr></tfoot>
      </table>

      <div className="print-purchase-order-signatures">
        <div><strong>PREPARED BY:</strong> {dash(generatedBy?.name)}<br />Date: {formatPrintDateTime(new Date())}</div>
      </div>
    </div>
  );
};
